import {
  Star,
  Target,
  ListTodo,
  Settings,
  LayoutGrid,
  MessageCircle,
  Clock,
  Sun,
  BookOpen,
  Wrench,
  Repeat,
  UserRound,
  MapPin,
  Compass,
  TrendingUp,
  Activity,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { BRAND } from '../../config/brand'

export interface NavItem {
  name: string
  href: string
  icon: ComponentType<{ className?: string }>
  badge?: string
  description?: string
}

export interface NavGroup {
  name: string
  icon: ComponentType<{ className?: string }>
  items: NavItem[]
  defaultOpen?: boolean
}

/** Three Rooms shell (owner pick 2026-08-25, design/app-shell canvas).
 *
 * The rail stopped being a directory of entity types (19 links, 7 groups —
 * one per database table) and became a posture: you are always in one of
 * three rooms. `match` prefixes decide which room a route lights up, so
 * every deep page still claims a home without owning a rail slot.
 *
 * Rooms unfolded (owner pick 2026-08-26, design/app-shell/RailIA): children
 * live ON the room now instead of in side-tables that only appeared while
 * their parent was active. Hiding them until you were already inside was the
 * "Life Map buries everything" complaint — you had to guess that Dreams
 * existed before you could navigate to it. The rail is taller as a result
 * and that is the intended trade.
 */
export interface RoomItem {
  name: string
  href: string
  icon: ComponentType<{ className?: string }>
  /** Quiet one-line descriptor under the room name. */
  sub: string
  /** Path prefixes that light this room up. */
  match: string[]
  /** Which --room-* hue this room wears, including its children. */
  hue: 'today' | 'map' | 'compass'
  /** Always-visible sub-links. Absent for rooms that are a single place. */
  children?: NavItem[]
}

export const rooms: RoomItem[] = [
  {
    name: 'Today',
    href: '/today',
    icon: Sun,
    sub: 'your day, one clear move',
    match: ['/today'],
    hue: 'today',
  },
  {
    name: 'Life Map',
    href: '/life-map',
    icon: LayoutGrid,
    sub: "everything you're growing",
    match: [
      '/life-map', '/dreams', '/goals', '/habits', '/skills', '/tasks',
      '/my-people', '/places', '/my-hierarchy', '/hierarchy-treemap',
    ],
    hue: 'map',
    children: [
      { name: 'Dreams', href: '/dreams', icon: Star },
      { name: 'Goals', href: '/goals', icon: Target },
      { name: 'Habits', href: '/habits', icon: Repeat },
      { name: 'Skills', href: '/skills', icon: Wrench },
      { name: 'Tasks', href: '/tasks', icon: ListTodo },
      { name: 'People', href: '/my-people', icon: UserRound },
      { name: 'Places', href: '/places', icon: MapPin },
    ],
  },
  {
    // The room where the assistant lives. Named after it (2026-09-06): the
    // name then has one home instead of leaking into copy everywhere, and
    // "Compass" stops being a third word for the chat.
    name: BRAND.assistant,
    href: '/horton-ai',
    icon: Compass,
    sub: BRAND.assistantSub,
    match: ['/horton-ai', '/diary', '/horton'],
    hue: 'compass',
    children: [
      { name: 'Talk', href: '/horton-ai', icon: MessageCircle },
      { name: 'Diary', href: '/diary', icon: BookOpen },
    ],
  },
]

/** Views of the map. Still exported because the diary sidebar and the mobile
 *  "More" drawer render a flattened chapter list, but the rooms above own
 *  the definition so the two views cannot drift apart. */
export const lifeMapSubLinks: NavItem[] =
  rooms.find((r) => r.hue === 'map')?.children ?? []

/** The two moods of the Compass room. */
export const compassSubLinks: NavItem[] =
  rooms.find((r) => r.hue === 'compass')?.children ?? []

/** The quiet utility row under the rooms. */
export const utilityItems: NavItem[] = [
  { name: 'Schedule', href: '/schedule', icon: Clock },
  { name: 'Progress', href: '/dashboard', icon: TrendingUp },
  // When you do things, when you are around, when you are quiet (2026-09-15).
  { name: 'Rhythm', href: '/analytics', icon: Activity },
]

/** Pruned grouped view — feeds the mobile "More" drawer and the diary
 *  theme's chapter-style sidebar. Same IA as the rooms, flattened. */
export const navigationGroups: NavGroup[] = [
  {
    name: 'Rooms',
    icon: Sun,
    defaultOpen: true,
    items: [
      { name: 'Today', href: '/today', icon: Sun },
      { name: 'Life Map', href: '/life-map', icon: LayoutGrid },
      // Same destination the Compass room calls "Talk" — one name, and a
      // brand-neutral one, since the visible product name is themed
      // (branding.name), not hardcoded (2026-08-29).
      { name: 'Talk', href: '/horton-ai', icon: MessageCircle },
      { name: 'Diary', href: '/diary', icon: BookOpen },
    ],
  },
  {
    name: 'Map views',
    icon: LayoutGrid,
    defaultOpen: true,
    items: lifeMapSubLinks,
  },
  {
    name: 'Plan & reflect',
    icon: Clock,
    defaultOpen: true,
    items: utilityItems,
  },
]

export const settingsItem: NavItem = {
  name: 'Settings',
  href: '/profile',
  icon: Settings,
}
